import { Button, Checkbox, Modal, Tag } from "antd";
import React, { useEffect, useState } from "react";

/**
 * SelectEstimateProvidersModal — multi-select picker of PROVIDER ACCOUNTS to
 * fetch an estimate under.
 *
 * A predetermination only appears under the portal account of the provider who
 * submitted it, so when an office has several accounts for the carrier's portal
 * the user picks which one(s) to fetch under — one scrape is dispatched per
 * selected account. Shown only when >1 account exists; a single account is used
 * directly by the caller without opening this modal.
 *
 * `accounts` are OfficePortalCredential rows that have a provider:
 *   { id, provider: { id, name }, is_default }
 * onConfirm receives the selected PROVIDER ids.
 */
export default function SelectEstimateProvidersModal({
  open,
  onCancel,
  onConfirm, // (selectedProviderIds) => void
  accounts,
  defaultSelectedIds,
  confirmLoading,
}) {
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(
    function () {
      if (open) setSelectedIds(Array.isArray(defaultSelectedIds) ? defaultSelectedIds : []);
    },
    [open, defaultSelectedIds],
  );

  function toggle(id) {
    setSelectedIds(function (prev) {
      return prev.includes(id) ? prev.filter(function (x) { return x !== id; }) : prev.concat([id]);
    });
  }

  const rows = (accounts || []).filter(function (a) { return a && a.provider; });

  return (
    <Modal open={open} onCancel={onCancel} footer={null} title="Fetch estimate — choose provider account(s)">
      <div style={{ color: "#6f6c80", marginBottom: 10, fontSize: 13 }}>
        An estimate only shows under the account of the provider who submitted it. Pick the account(s) to fetch under — one
        fetch runs per account.
      </div>
      <div
        style={{ maxHeight: 288, display: "flex", flexDirection: "column", gap: 10, overflowY: "auto", padding: 10 }}
        className="custom-scroll"
      >
        {rows.map(function (acct) {
          const providerId = acct.provider.id;
          const isSelected = selectedIds.includes(providerId);
          return (
            <div
              key={acct.id}
              onClick={function () { toggle(providerId); }}
              style={{
                width: "100%",
                boxShadow: isSelected ? "0 0 10px rgba(107, 67, 181, 0.88)" : "0 0 10px 0 rgba(0, 0, 0, 0.15)",
                padding: "10px 16px",
                borderRadius: 5,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <Checkbox
                checked={isSelected}
                onClick={function (e) { e.stopPropagation(); }}
                onChange={function () { toggle(providerId); }}
              />
              <div style={{ display: "flex", width: "100%", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <div>{acct.provider.name}</div>
                {acct.is_default ? <Tag color="blue">Default</Tag> : null}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "end", gap: 10, marginTop: 12 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          type="primary"
          loading={confirmLoading}
          disabled={selectedIds.length === 0}
          onClick={function () { onConfirm(selectedIds); }}
        >
          Fetch ({selectedIds.length})
        </Button>
      </div>
    </Modal>
  );
}
